<template>
  <v-card :color="$vuetify.theme.dark ? 'blue-darken-2 ' : 'white-lighten-2'">
    <v-row class="no-gutters">
      <v-col cols="12" sm="6" md="6" lg="3">
        <v-text-field v-model="search" @input="debounceSearch" prepend-icon="mdi-magnify" label="Search here ..."
          single-line solo hide-details>
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="2">
        <v-select solo :items="allAccounts()" v-model="trading_account_type_id" @input="accountFilter"
          append-icon="mdi-chevron-down" label="Account Types" class="custom-select" item-text="account_type_display"
          item-value="id"></v-select>
      </v-col>

      <v-col cols="12" sm="6" md="6" lg="2">
        <v-dialog ref="dialog" v-model="modal" :return-value.sync="date" width="290px" class="">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="selectedDatesText" placeholder="Date filter" prepend-icon="mdi-calendar"
              append-icon="mdi-chevron-down" readonly v-bind="attrs" v-on="on" solo
              class="custom-select"></v-text-field>
          </template>
          <v-date-picker v-model="selectedDates" class="custom-date-picker" scrollable range>
            <v-spacer></v-spacer>
            <v-btn :color="$vuetify.theme.dark ? '#ff567f' : '#ff567f'" text @click="cancelSelection">
              Cancel
            </v-btn>
            <v-btn :color="$vuetify.theme.dark ? '#fff' : '#131c29'" text @click="confirmSelection">
              OK
            </v-btn>
          </v-date-picker>
        </v-dialog>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-btn @click="fetchData" :disabled="isLoading" small :ripple="false" tile elevation="0" class="custom-btn"
          height="39" style="margin-top: 29px; color: white" color="#039de9">
          <template v-if="isLoading">
            <v-progress-circular indeterminate size="15" color="white" style="margin-right: 5px;"></v-progress-circular>
            Fetching<span class="dots"></span>
          </template>
          <template v-else>
            <v-icon color="#fff" left>mdi-refresh</v-icon>
            Refresh
          </template>
        </v-btn>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="2">
        <ul>
          <li>
            <span>Total Lot:</span>
            <span>{{ formattedAllNumber(summaries?.totalVolume) || 0 }}</span>
          </li>
          <li>
            <span class="profit-loss">Profit / Loss:</span>
            <span>{{ formattedAllNumber(summaries?.totalProfit) || 0 }}</span>
          </li>
        </ul>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12">
        <v-card class="rounded-0 elevation-0" :color="$vuetify.theme.dark ? '#172233' : '#E8F1F8'">
          <v-sheet class="transactionTable depositTableForTransaction"
            :color="$vuetify.theme.dark ? '#1A2A3E ' : '#F1F7FB'">
            <v-data-table :headers="headers" :items="trades" :server-items-length="pagination?.total || 10"
              :options.sync="filterOptions" :loading="isLoading" :items-per-page="filterOptions.itemsPerPage"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 200],
                'items-per-page-text': 'Rows per page:'
              }" @update:options="handleOptionsChange" disable-sort>
              <template v-slot:loading>
                <div class="d-flex align-center justify-center pa-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <span class="ml-2">Loading data...</span>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="d-flex align-center justify-center pa-4">
                  <v-icon color="secondary">mdi-information</v-icon>
                  <span class="ml-2">No data found</span>
                </div>
              </template>
              <template v-slot:no-results>
                <div class="d-flex align-center justify-center pa-4">
                  <v-icon color="secondary">mdi-magnify</v-icon>
                  <span class="ml-2">No matching data found</span>
                </div>
              </template>
              <template v-slot:item.fullName="{ item }">
                <router-link v-if="$canRoute('registered-user?tab=1') === 1" class="text-capitalize text--ash" :to="`/registered-user-details/${item?.client_id}`">
                  {{ item.fullName }} ({{ item?.client_id }})
                </router-link>
                <span v-else class="text-capitalize text--ash">{{ item.fullName }} ({{ item?.client_id }})</span>
              </template>
              <template v-slot:item.login="{ item }">
                {{ item?.login + " (" + (item.trading_account_type ? item.trading_account_type.substring(0, 3) : "") +
                  ")" }}
              </template>
              <template v-slot:item.action="{ item }">
                <span :style="{
                  color: item?.action === 0 ? '#4169E1' : item?.action === 1 ? '#FF4040' : 'inherit'
                }">
                  {{ item?.action === 0 ? "Buy" : item?.action === 1 ? "Sell" : null }}
                </span>
              </template>
            </v-data-table>
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12">
        <v-card class="rounded-0 elevation-0 chart-tab" :color="$vuetify.theme.dark ? '#172233' : '#E8F1F8'">
          <v-card-title>
            <v-icon class="blink" color="#FF567F"> mdi-circle-small </v-icon>
            Closed Trades Profit / Loss History
          </v-card-title>
          <v-sheet class="pb-3" :color="$vuetify.theme.dark ? '#1A2A3E ' : '#F1F7FB'">
            <apexChart width="100%" height="338px" type="bar" :options="options" :series="series"
              class="liveTradeChart">
            </apexChart>
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { formatChartDate, formatMySQLDate } from "@/_helpers/formateDate";
import { formatNumber } from "@/_helpers/formatNumber";
import toast from "@/services/Notification";
import {
    ACCOUNTTYPES__ACTIONS,
    CLOSEDTRADES__ACTIONS
} from "@/store/action-types";
import { mapActions, mapState } from "vuex";

export default {
  data() {
    return {
      search: "",
      searchTimeout: null,
      selectedDates: [],
      selectedDatesText: null,
      filterOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      trading_account_type_id: null,
      headers: [
        { text: "Name", align: "start", value: "fullName" },
        { text: "Account", value: "login" },
        { text: "Ticket", value: "position_id" },
        { text: "Time", value: "time" },
        { text: "Lots", value: "volume" },
        { text: "Symbol", value: "symbol" },
        { text: "Open Price", value: "price" },
        { text: "Closed Price", value: "price_position" },
        { text: "Profit", value: "profit" },
        { text: "Type", value: "action" },
      ],
      modal: false,
      options: {
          chart: {
            id: "vuechart-example",
            type: "bar",
            height: "338px",
            theme: "dark",
          },
          colors: [],
          names: [],
          xaxis: {
            categories: [],
            labels: {
              show: true,
              style: {
                colors: "#708AA7",
                fontSize: '12px',
              },
              rotate: -75,
              rotateAlways: true
            },
            axisTicks: {
              show: false,
            },
            axisBorder: {
              show: false,
            },
          },
          yaxis: {
            min: 0,
            labels: {
              style: {
                colors: "#647E98",
                fontSize: '12px',
              },
              formatter: function(value) {
                return Math.abs(value).toFixed(2);
              }
            }
          },
          tooltip: {
            y: {
              formatter: function(value, {dataPointIndex, w}) {
                const name = w.config.names[dataPointIndex];
                const color = w.config.colors[dataPointIndex];
                const cappedValue = Number(value).toFixed(2);
                const data = name + ' : ' + cappedValue;
                
                return  `<span style="color: ${color}"> ${data} </span>`;
              }
            },
          },
          plotOptions: {
            bar: {
              columnWidth: "40%",
              distributed: true
            }
          },
          dataLabels: {
            enabled: false,
          },
          grid: {
            show: false,
          },
          legend: {
            position: "top",
            horizontalAlign: "left",
            show: true,
            showForSingleSeries: true,
            customLegendItems: ["Profit", "Loss"],
            labels: {
              colors: ["#039de9", "#647E98"],
              useSeriesColors: false,
            },
            markers: {
              fillColors: ["#039de9", "#647E98"],
              radius: 12,
            },
          },
        },
        series: [{
          name: "",
          data: []
        }],
        refreshInterval: null,
        isTabVisible: true
    };
  },

  computed: {
    ...mapState("accounttypes", ["accounttypes"]),
    ...mapState("closedtrades", [
      "trades",
      "chartData",
      "summaries",
      "pagination",
      "isLoading",
    ]),
    ...mapState("admins", ["loggedAdmin"]),
  },

  watch: {
    chartData: {
      handler: "updateChartData",
      deep: true,
      immediate: true,
    }
  },

  methods: {
    formatMySQLDate,
    ...mapActions("closedtrades", [CLOSEDTRADES__ACTIONS.CLOSEDTRADES, CLOSEDTRADES__ACTIONS.CLOSEDTRADESCHARTDATA]),
    ...mapActions("accounttypes", [ACCOUNTTYPES__ACTIONS.ACCOUNTTYPES]),

    formattedAllNumber(num) {
      return formatNumber(num || 0);
    },

    async fetchData() {
      const parameter = {
        page: this.filterOptions.page || 1,
        limit: this.filterOptions.itemsPerPage || 10,
        search: this.search,
        trading_account_type_id: this.trading_account_type_id || null,
      };

      if (this.selectedDates?.length === 2) {
        parameter.duration = `${this.selectedDates[0]}to${this.selectedDates[1]}`;
      }

      try {
        await this[CLOSEDTRADES__ACTIONS.CLOSEDTRADES](parameter);
        await this[CLOSEDTRADES__ACTIONS.CLOSEDTRADESCHARTDATA]();
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data. Please try again.");
      }
    },

    allAccounts() {
      const formattedAccounts = this.accounttypes.map(account => ({
        id: account.id,
        account_type_display: account.account_type_display
      }));
      return [{ id: "All", account_type_display: "All" }, ...formattedAccounts];
    },

    async handleSearch(value) {
      await this.fetchData();
    },

    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.handleSearch();
      }, 500);
    },

    handleOptionsChange(options) {
      this.fetchData();
    },

    getCurrentDate() {
      return new Date().toISOString().split("T")[0];
    },
    generateLast30Days() {
      const dates = [];
      for(let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dates.push(formatChartDate(date.toISOString()));
      }
      return dates;
    },

    updateChartData() {
      if (!this.chartData || !this.chartData.length) {
        this.series = [{
          name: "",
          data: this.options.xaxis.categories.map(() => 0)
        }];
        return;
      }

      // Create a mapping of date to profit value
      const dataMap = {};
      this.chartData.forEach(item => {
        if (item?.trade_date) {
          const tradeDate = formatChartDate(item.trade_date);
          if (tradeDate) {
            dataMap[tradeDate] = parseFloat(item.total_profit || 0);
          }
        }
      });
      
      const originalValues = this.options.xaxis.categories.map(date => {
        return date ? (dataMap[date] !== undefined ? dataMap[date] : 0) : 0;
      });

      const names = originalValues.map(value => {
        if(value < 0) return "Loss";
        return "Profit";
      });

      const displayValues = originalValues.map(value => Math.abs(value));
      
      this.series = [{
        name: "",
        data: displayValues
      }];
      
      const newColors = originalValues.map(value => {
        if (value < 0) {
          return '#647E98'; // Loss color
        } else {
          return '#039de9'; // Profit color 
        }
      });

      this.options = {
        ...this.options,
        colors: newColors,
        names
      };


    },

    cancelSelection() {
      this.modal = false;
      this.selectedDates = [];
      this.selectedDatesText = null
      this.fetchData();
    },

    confirmSelection() {
      if (this.selectedDates.length !== 2) {
        toast.error("Please select both start and end dates");
        return;
      }

      let [startDate, endDate] = this.selectedDates;
      if (new Date(startDate) > new Date(endDate)) {
        [startDate, endDate] = [endDate, startDate];
      }

      this.selectedDatesText = `${startDate} ~ ${endDate}`;
      this.modal = false;
      this.filterOptions.page = 1;
      this.fetchData();
    },

    accountFilter() {
      this.filterOptions.page = 1;
      this.fetchData();
    },
  },
  created() {
      this[ACCOUNTTYPES__ACTIONS.ACCOUNTTYPES]();
    },

  mounted() {
    // Initialize xaxis categories
    this.options.xaxis.categories = this.generateLast30Days();
    
    // Initial data fetch
    this.fetchData();
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
};
</script>

<style scoped>
.dots::after {
  content: "";
  display: inline-block;
  animation: dotsAnimation 1.5s steps(4, end) infinite;
}

@keyframes dotsAnimation {
  0% {
    content: "";
  }

  25% {
    content: ".";
  }

  50% {
    content: "..";
  }

  75% {
    content: "...";
  }

  100% {
    content: "";
  }
}
</style>